<template>
  <Card class="expense-sheet-mobile-header">
    <template #content>
      <div class="header-row">
        <span class="creation-date">{{ formatOnlyDate(data.creationDate) }}</span>
      </div>
      
      <div class="header-row amount-row">
        <span class="label">{{ t('wkt.client.expensesheets.detail-window.amount') }}</span>
        <h3 class="amount-value">{{ formatCurrency(data.amount) }}</h3>
      </div>
      
      <div class="header-row">
        <span class="project-name">{{ data.projectName || '--' }}</span>
        <span 
          class="status-text" 
          :style="{ color: getStatusColor(Number(data.status)) }"
          :class="{ 'p-tag-forthcoming': Number(data.status) === -1 }"
        >
          {{ getStatusText(Number(data.status)) }}
        </span>
      </div>
    </template>
    
    <template v-if="[-1, 0, 2].includes(Number(data.status))" #footer>
      <div class="mobile-footer">
        <Button
          v-if="[-1, 2].includes(Number(data.status))"
          :label="t('wkt.client.expensesheets.detail-window.button-edit')"
          class="footer-primary-button"
          color="#185EA9"
          variant="text"
          @click="$emit('edit-expense-sheet')"
        />
        <div class="spacer"></div>
        <Button
          v-if="[-1, 2].includes(Number(data.status))"
          :label="t('wkt.client.expensesheets.detail-window.button-send')"
          class="footer-button-cancel"
          variant="text"
          :disabled="!hasItems"
          @click="$emit('confirm-send-expense-sheet')"
        />
        <Button
          v-if="[0].includes(Number(data.status))"
          :label="t('wkt.client.expensesheets.detail-window.button-cancel_request')"
          class="footer-primary-button"
          color="#185EA9"
          variant="text"
          :disabled="!isEmployeeCreator"
          @click="$emit('cancel-expense-sheet-request')"
        />
      </div>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDateFormatter } from '@wkt/composables/useDateFormatter'
import { useExpenseSheets } from '@wkt/composables/useExpenseSheets'
import { useUtils } from '@wkt/composables/useUtils'

// Props
interface Props {
  data: any
  employeeId: number | null
  hasItems: boolean
}

const props = defineProps<Props>()

// Emits
defineEmits<{
  'edit-expense-sheet': []
  'confirm-send-expense-sheet': []
  'cancel-expense-sheet-request': []
}>()

// Composables
const { t } = useI18n()
const { formatOnlyDate } = useDateFormatter()
const { getStatusText, getStatusColor } = useExpenseSheets()
const { formatCurrency } = useUtils()

// Computed
const isEmployeeCreator = computed(() => {
  return props.employeeId && props.employeeId === props.data.creatorId
})
</script>

<style scoped>
.expense-sheet-mobile-header {
  background-color: var(--light-blue);
  border: 1px solid #B8CFDD;
  border-radius: 8px;
  box-shadow: none;
}

.expense-sheet-mobile-header :deep(.p-card-body) {
  padding: 0;
}

.expense-sheet-mobile-header :deep(.p-card-content) {
  padding: 0.75rem;
  padding-bottom: 0.5rem;
}

.expense-sheet-mobile-header :deep(.p-card-footer) {
  padding: 0;
  border-top: 1px solid #B8CFDD;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.header-row:last-child {
  margin-bottom: 0;
}

.creation-date {
  color: grey;
  font-size: 14px;
}

.amount-row {
  padding: 0.25rem 0;
}

.label {
  color: #000;
  font-size: 16px;
}

.amount-value {
  color: #000;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.project-name {
  color: grey;
  font-size: 14px;
}

.status-text {
  font-size: 14px;
  white-space: nowrap;
}

.status-text.p-tag-forthcoming {
  color: #424242 !important;
  font-weight: 400;
}

.mobile-footer {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
}

.spacer {
  flex: 1;
}
</style>